/**
 * AdditionalNotes Component
 * 
 * A modern, professional note-taking interface for matter-related notes.
 * Features smooth animations, proper loading/empty states, and accessible design.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faStickyNote, 
  faSpinner,
  faSearch,
  faTimes,
  faCalendarAlt,
  faExpand,
  faSortAmountDown,
  faSortAmountUp,
  faFilter,
  faPen,
  faTrash,
  faCheck,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import TopBar from './Components/TopBar';
import FormModal from '@frontend/Components/Modals/FormModal/FormModal';
import NotesService from '@frontend/AppSettings/services/NotesService';
import './AdditionalNotes.css';

interface Note {
  noteid: number;
  note_text: string;
  created_at: string;
  updated_at?: string;
  sept_tenantid?: string;
  title?: string | null;
}

/**
 * AdditionalNotesContent
 * 
 * The core notes UI (header, search, form, grid, modal) without any page wrapper.
 * Can be embedded in any parent layout (MatterDetails tabs, standalone route, etc.)
 */
export const AdditionalNotesContent: React.FC = () => {
  const { matterId } = useParams<{ matterId: string }>();
  
  // State
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter & sort state
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [newNote, setNewNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Modal state
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);

  // Edit state
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editText, setEditText] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Delete state
  const [deletingNoteId, setDeletingNoteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch notes
  const fetchNotes = useCallback(async () => {
    if (!matterId) return;
    
    try {
      setIsLoading(true);
      setError(null);
      const data = await NotesService.getNotesByMatterId(matterId);
      setNotes(data || []);
    } catch (err) {
      console.error('Error fetching notes:', err);
      setError('Failed to load notes. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [matterId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // Filter notes based on search + date filter
  const filteredNotes = useMemo(() => {
    let result = notes;

    // Text search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(note => 
        note.note_text.toLowerCase().includes(query) ||
        (note.title && note.title.toLowerCase().includes(query))
      );
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      let cutoff: Date;

      switch (dateFilter) {
        case 'today':
          cutoff = startOfToday;
          break;
        case 'week':
          cutoff = new Date(startOfToday.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          cutoff = new Date(startOfToday.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        default:
          cutoff = new Date(0);
      }

      result = result.filter(note => new Date(note.created_at) >= cutoff);
    }

    return result;
  }, [notes, searchQuery, dateFilter]);

  // Sort notes
  const sortedNotes = useMemo(() => {
    return [...filteredNotes].sort((a, b) => {
      const timeA = new Date(a.created_at).getTime();
      const timeB = new Date(b.created_at).getTime();
      return sortOrder === 'newest' ? timeB - timeA : timeA - timeB;
    });
  }, [filteredNotes, sortOrder]);

  // Check if any filters are active
  const hasActiveFilters = dateFilter !== 'all' || searchQuery.trim() !== '';

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setDateFilter('all');
    setSortOrder('newest');
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      })}`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNote.trim() || !matterId) return;

    try {
      setIsSubmitting(true);
      const noteData: { note_text: string; title?: string } = { note_text: newNote.trim() };
      if (newNoteTitle.trim()) {
        noteData.title = newNoteTitle.trim();
      }
      await NotesService.addMatterNote(matterId, noteData);
      await fetchNotes();
      setNewNoteTitle('');
      setNewNote('');
      setShowForm(false);
    } catch (err) {
      console.error('Error submitting note:', err);
      setError('Failed to add note. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle form cancel
  const handleCancel = () => {
    setNewNoteTitle('');
    setNewNote('');
    setShowForm(false);
  };

  // Start editing a note
  const startEdit = (note: Note) => {
    setEditingNote(note);
    setEditTitle(note.title || '');
    setEditText(note.note_text);
    setSelectedNote(null); // close detail modal if open
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingNote(null);
    setEditTitle('');
    setEditText('');
  };

  // Submit edit
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editText.trim() || !matterId || !editingNote) return;

    try {
      setIsUpdating(true);
      const noteData: { note_text: string; title?: string } = { note_text: editText.trim() };
      if (editTitle.trim()) {
        noteData.title = editTitle.trim();
      }
      await NotesService.updateMatterNote(matterId, editingNote.noteid, noteData);
      await fetchNotes();
      cancelEdit();
    } catch (err) {
      console.error('Error updating note:', err);
      setError('Failed to update note. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Request delete confirmation
  const confirmDelete = (noteId: number) => {
    setDeletingNoteId(noteId);
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeletingNoteId(null);
  };

  // Execute delete
  const handleDelete = async () => {
    if (!matterId || deletingNoteId === null) return;

    try {
      setIsDeleting(true);
      await NotesService.deleteMatterNote(matterId, deletingNoteId);
      // Close any open modals referencing this note
      if (selectedNote?.noteid === deletingNoteId) setSelectedNote(null);
      if (editingNote?.noteid === deletingNoteId) cancelEdit();
      await fetchNotes();
      setDeletingNoteId(null);
    } catch (err) {
      console.error('Error deleting note:', err);
      setError('Failed to delete note. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Truncate text for card preview
  const truncateText = (text: string, maxLength: number = 180): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  return (
    <>
      <div className="md-notes-page">
        {/* Header Section */}
        <div className="md-notes-header">
          <div className="md-notes-header-left">
            <h1 className="md-notes-title">
              <FontAwesomeIcon icon={faStickyNote} className="md-notes-title-icon" />
              Notes
            </h1>
            <span className="md-notes-count">
              {notes.length} {notes.length === 1 ? 'note' : 'notes'}
            </span>
          </div>
          
          <div className="md-notes-header-right">
            {/* Search */}
            <div className="md-notes-search">
              <FontAwesomeIcon icon={faSearch} className="md-notes-search-icon" />
              <input
                type="text"
                placeholder="Search notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="md-notes-search-input"
              />
              {searchQuery && (
                <button 
                  className="md-notes-search-clear"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
            
            {/* Add Button */}
            <button
              className="md-notes-add-button"
              onClick={() => setShowForm(true)}
              aria-label="Add new note"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Add Note</span>
            </button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="md-notes-filters">
          <div className="md-notes-filters-left">
            <FontAwesomeIcon icon={faFilter} className="md-notes-filters-icon" />

            <div className="md-notes-filter-group">
              <label className="md-notes-filter-label">Date</label>
              <select
                className="md-notes-filter-select"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value as 'all' | 'today' | 'week' | 'month')}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Past 7 Days</option>
                <option value="month">Past 30 Days</option>
              </select>
            </div>

            <button
              className={`md-notes-sort-button ${sortOrder === 'oldest' ? 'md-notes-sort-button--active' : ''}`}
              onClick={() => setSortOrder(prev => prev === 'newest' ? 'oldest' : 'newest')}
              aria-label={`Sort by ${sortOrder === 'newest' ? 'oldest' : 'newest'} first`}
              title={sortOrder === 'newest' ? 'Newest first' : 'Oldest first'}
            >
              <FontAwesomeIcon icon={sortOrder === 'newest' ? faSortAmountDown : faSortAmountUp} />
              <span>{sortOrder === 'newest' ? 'Newest' : 'Oldest'}</span>
            </button>
          </div>

          <div className="md-notes-filters-right">
            {hasActiveFilters && (
              <button className="md-notes-clear-filters" onClick={clearFilters}>
                <FontAwesomeIcon icon={faTimes} />
                Clear Filters
              </button>
            )}
            <span className="md-notes-results-count">
              {filteredNotes.length === notes.length
                ? `${notes.length} ${notes.length === 1 ? 'note' : 'notes'}`
                : `${filteredNotes.length} of ${notes.length}`}
            </span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="md-notes-error">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="md-notes-error-dismiss">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}

        {/* Add Note Form */}
        {showForm && (
          <div className="md-notes-form-container">
            <form onSubmit={handleSubmit} className="md-notes-form">
              <div className="md-notes-form-header">
                <h3>New Note</h3>
                <span className="md-notes-form-char-count">
                  {newNote.length} characters
                </span>
              </div>
              <input
                type="text"
                className="md-notes-form-title-input"
                placeholder="Note title (optional)"
                value={newNoteTitle}
                onChange={(e) => setNewNoteTitle(e.target.value)}
                autoFocus
                disabled={isSubmitting}
              />
              <textarea
                className="md-notes-form-textarea"
                placeholder="Write your note here..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={4}
                disabled={isSubmitting}
              />
              <div className="md-notes-form-actions">
                <button 
                  type="button" 
                  className="md-notes-form-cancel"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="md-notes-form-submit"
                  disabled={!newNote.trim() || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPlus} />
                      <span>Add Note</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Notes Grid */}
        <div className="md-notes-content">
          {isLoading ? (
            <div className="md-notes-loading">
              <FontAwesomeIcon icon={faSpinner} spin className="md-notes-loading-icon" />
              <span>Loading notes...</span>
            </div>
          ) : sortedNotes.length === 0 ? (
            <div className="md-notes-empty">
              <div className="md-notes-empty-icon">
                <FontAwesomeIcon icon={faStickyNote} />
              </div>
              {hasActiveFilters ? (
                <>
                  <h3>No notes found</h3>
                  <p>No notes match your current filters{searchQuery ? ` for "${searchQuery}"` : ''}.</p>
                  <button 
                    className="md-notes-empty-action"
                    onClick={clearFilters}
                  >
                    Clear Filters
                  </button>
                </>
              ) : (
                <>
                  <h3>No notes yet</h3>
                  <p>Add your first note to keep track of important information about this matter.</p>
                  <button 
                    className="md-notes-empty-action"
                    onClick={() => setShowForm(true)}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Add Your First Note
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="md-notes-grid">
              {sortedNotes.map((note, index) => (
                <article
                  key={note.noteid}
                  className="md-note-card"
                  onClick={() => setSelectedNote(note)}
                  style={{ animationDelay: `${index * 0.05}s` }}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setSelectedNote(note)}
                  role="button"
                  aria-label={`View note${note.title ? `: ${note.title}` : ''} from ${formatDate(note.created_at)}`}
                >
                  <div className="md-note-card-header">
                    <span className="md-note-card-date">
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      {formatDate(note.created_at)}
                    </span>
                    <div className="md-note-card-actions">
                      <button 
                        className="md-note-card-action-btn md-note-card-edit-btn"
                        aria-label="Edit note"
                        title="Edit note"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEdit(note);
                        }}
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button 
                        className="md-note-card-action-btn md-note-card-delete-btn"
                        aria-label="Delete note"
                        title="Delete note"
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmDelete(note.noteid);
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                      <button 
                        className="md-note-card-expand"
                        aria-label="Expand note"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedNote(note);
                        }}
                      >
                        <FontAwesomeIcon icon={faExpand} />
                      </button>
                    </div>
                  </div>
                  {note.title && (
                    <h3 className="md-note-card-title">{note.title}</h3>
                  )}
                  <p className="md-note-card-content">
                    {truncateText(note.note_text)}
                  </p>
                  <div className="md-note-card-footer">
                    <span className="md-note-card-preview-hint">Click to view full note</span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Note Detail Modal */}
      <FormModal
        isOpen={!!selectedNote}
        onClose={() => setSelectedNote(null)}
        title={selectedNote?.title || 'Note Details'}
        size="medium"
      >
        {selectedNote && (
          <div className="md-note-detail">
            <div className="md-note-detail-meta">
              <span className="md-note-detail-date">
                <FontAwesomeIcon icon={faCalendarAlt} />
                Created: {new Date(selectedNote.created_at).toLocaleString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}
              </span>
            </div>
            <div className="md-note-detail-content">
              {selectedNote.note_text}
            </div>
            <div className="md-note-detail-actions">
              <button
                className="md-note-detail-edit-btn"
                onClick={() => startEdit(selectedNote)}
              >
                <FontAwesomeIcon icon={faPen} />
                <span>Edit</span>
              </button>
              <button
                className="md-note-detail-delete-btn"
                onClick={() => {
                  confirmDelete(selectedNote.noteid);
                  setSelectedNote(null);
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
                <span>Delete</span>
              </button>
            </div>
          </div>
        )}
      </FormModal>

      {/* Edit Note Modal */}
      <FormModal
        isOpen={!!editingNote}
        onClose={cancelEdit}
        title="Edit Note"
        size="medium"
      >
        {editingNote && (
          <form onSubmit={handleEditSubmit} className="md-notes-edit-form">
            <div className="md-notes-form-header">
              <h3>Edit Note</h3>
              <span className="md-notes-form-char-count">
                {editText.length} characters
              </span>
            </div>
            <input
              type="text"
              className="md-notes-form-title-input"
              placeholder="Note title (optional)"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              autoFocus
              disabled={isUpdating}
            />
            <textarea
              className="md-notes-form-textarea"
              placeholder="Write your note here..."
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={6}
              disabled={isUpdating}
            />
            <div className="md-notes-form-actions">
              <button 
                type="button" 
                className="md-notes-form-cancel"
                onClick={cancelEdit}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="md-notes-form-submit"
                disabled={!editText.trim() || isUpdating}
              >
                {isUpdating ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </FormModal>

      {/* Delete Confirmation Modal */}
      <FormModal
        isOpen={deletingNoteId !== null}
        onClose={cancelDelete}
        title="Delete Note"
        size="small"
      >
        <div className="md-note-delete-confirm">
          <div className="md-note-delete-confirm-icon">
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </div>
          <h3>Are you sure?</h3>
          <p>This action cannot be undone. This note will be permanently deleted.</p>
          <div className="md-note-delete-confirm-actions">
            <button
              className="md-notes-form-cancel"
              onClick={cancelDelete}
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              className="md-note-delete-confirm-btn"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Deleting...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faTrash} />
                  <span>Delete Note</span>
                </>
              )}
            </button>
          </div>
        </div>
      </FormModal>
    </>
  );
};

/**
 * AdditionalNotes (default export)
 * 
 * Full-page version with TopBar — used by the standalone /additionalNotes route.
 */
const AdditionalNotes: React.FC = () => {
  return (
    <div className="Tab-Container">
      <TopBar />
      <AdditionalNotesContent />
    </div>
  );
};

export default AdditionalNotes;
